import React, { useState } from "react";
import { Send } from "lucide-react";
import { api } from "../../lib/api";
import {
  Section, TextField, TextAreaField, RadioField, CheckboxGroup,
  AcknowledgmentCheckbox, ACKNOWLEDGMENT_TEXT, Honeypot,
} from "./BookingFormFields";
import ScheduleField from "./ScheduleField";
import FileAttachmentField from "./FileAttachmentField";

const EVENT_TYPES = ["Stage Play", "Concert", "Comedy Show", "Film Screening", "Festival", "Spoken Word", "Other"];
const TICKET_MANAGER = ["Encore Theatre", "Event Producer", "Third Party Platform"];
const YN = ["Yes", "No"];
const TECH_NEEDS = [
  "Sound System", "Lighting Support", "Projection", "Box Office Support",
  "Front of House Staff", "Stage Manager", "Technical Operator",
  "Lobby Access", "Dressing Room Access", "Merchandise Area",
];
const CONTENT_FLAGS = [
  "Explicit sexual content", "Simulated violence", "Prop weapons",
  "Political messaging", "Religious messaging", "Smoking/Vaping",
  "Heavy profanity", "None of the above",
];

const EMPTY = {
  full_name: "", organization: "", email: "", phone: "",
  website: "", mailing_address: "",
  event_title: "", event_type: "", event_description: "",
  schedule: [],
  setup_time: "", breakdown_time: "",
  expected_attendance: "", ticket_price_range: "",
  ticket_manager: "", reserved_seating: "",
  produced_before: "", produced_where: "", previous_attendance: "",
  similar_venues: "", venue_references: "",
  tech_needs: [],
  food_served: "", food_description: "", outside_vendors: "",
  content_flags: [],
  why_encore: "", estimated_budget: "", success_definition: "",
  ever_canceled: "", canceled_explanation: "",
  attachments: [],
  hp_url: "",
  acknowledged: false,
};

export default function TicketedEventForm({ onSuccess, onError }) {
  const [f, setF] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.acknowledged) return onError?.("Please confirm the acknowledgment to submit.");
    setSubmitting(true);
    try {
      const { full_name, email, phone, organization, event_title, hp_url, ...rest } = f;
      await api.post("/booking", {
        form_type: "ticketed",
        full_name, email, phone, organization, event_title,
        hp_url,
        data: rest,
      });
      setF(EMPTY);
      onSuccess?.();
    } catch (err) {
      onError?.(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-12 relative" data-testid="ticketed-event-form">
      <Honeypot value={f.hp_url} onChange={set("hp_url")} testId="ticketed-hp-url" />
      <Section title="Contact Information">
        <TextField label="Full Name" value={f.full_name} onChange={set("full_name")} required testId="ticketed-full-name" autoComplete="name" />
        <TextField label="Organization / Production Company" value={f.organization} onChange={set("organization")} testId="ticketed-org" autoComplete="organization" />
        <TextField label="Email Address" type="email" value={f.email} onChange={set("email")} required testId="ticketed-email" autoComplete="email" />
        <TextField label="Phone Number" type="tel" value={f.phone} onChange={set("phone")} required testId="ticketed-phone" autoComplete="tel" />
        <TextField label="Website / Social Media Links" value={f.website} onChange={set("website")} fullWidth testId="ticketed-website" />
        <TextField label="Mailing Address" value={f.mailing_address} onChange={set("mailing_address")} fullWidth testId="ticketed-address" />
      </Section>

      <Section title="Event Information">
        <TextField label="Event Title" value={f.event_title} onChange={set("event_title")} required fullWidth testId="ticketed-event-title" />
        <RadioField label="Type of Event" value={f.event_type} onChange={set("event_type")} options={EVENT_TYPES} required testId="ticketed-event-type" />
        <TextAreaField label="Describe your event in detail" value={f.event_description} onChange={set("event_description")} required rows={5} testId="ticketed-description" />
        <ScheduleField
          label="Performance & Rehearsal Schedule"
          hint="Add a row for every show, rehearsal, tech day, or load-in. Tag each by type so we can build the full booking calendar at a glance."
          value={f.schedule}
          onChange={set("schedule")}
          testId="ticketed-schedule"
        />
        <TextField label="Set-Up Time Needed" value={f.setup_time} onChange={set("setup_time")} testId="ticketed-setup" placeholder="e.g. 2 hours before each call" />
        <TextField label="Breakdown Time Needed" value={f.breakdown_time} onChange={set("breakdown_time")} testId="ticketed-breakdown" placeholder="e.g. 1 hour after each show" />
        <TextField label="Expected Attendance" value={f.expected_attendance} onChange={set("expected_attendance")} required testId="ticketed-attendance" type="number" min={1} max={100} placeholder="Max 100" />
        <TextField label="Ticket Price Range" value={f.ticket_price_range} onChange={set("ticket_price_range")} testId="ticketed-price" placeholder="e.g. $15–$35" />
        <RadioField label="Who will manage ticket sales?" value={f.ticket_manager} onChange={set("ticket_manager")} options={TICKET_MANAGER} required testId="ticketed-manager" />
        <RadioField label="Will there be reserved seating?" value={f.reserved_seating} onChange={set("reserved_seating")} options={YN} required testId="ticketed-reserved" />
      </Section>

      <Section title="Production History">
        <RadioField label="Have you produced this event before?" value={f.produced_before} onChange={set("produced_before")} options={YN} required testId="ticketed-produced-before" />
        <TextField label="If yes, where?" value={f.produced_where} onChange={set("produced_where")} testId="ticketed-produced-where" />
        <TextField label="Previous attendance numbers" value={f.previous_attendance} onChange={set("previous_attendance")} testId="ticketed-prev-attendance" />
        <TextAreaField label="Please list 2–3 venues where you have presented similar work" value={f.similar_venues} onChange={set("similar_venues")} testId="ticketed-similar-venues" />
        <TextAreaField label="Venue references (names, titles, contact info)" value={f.venue_references} onChange={set("venue_references")} testId="ticketed-references" />
      </Section>

      <Section title="Technical & Venue Needs">
        <CheckboxGroup label="Check all that apply" values={f.tech_needs} onChange={set("tech_needs")} options={TECH_NEEDS} testId="ticketed-tech-needs" />
        <RadioField label="Will food be served?" value={f.food_served} onChange={set("food_served")} options={YN} required testId="ticketed-food" />
        {f.food_served === "Yes" && (
          <TextAreaField label="What type of food will be served?" value={f.food_description} onChange={set("food_description")} required testId="ticketed-food-description" placeholder="e.g. hors d'oeuvres, full plated dinner, food truck, etc." />
        )}
        <RadioField label="Will outside vendors be used?" value={f.outside_vendors} onChange={set("outside_vendors")} options={YN} required testId="ticketed-vendors" />
      </Section>

      <Section title="Event Content">
        <CheckboxGroup label="Does your event include any of the following?" values={f.content_flags} onChange={set("content_flags")} options={CONTENT_FLAGS} testId="ticketed-content-flags" />
      </Section>

      <Section title="Final Questions">
        <TextAreaField label="What about Encore Theatre made you choose this venue?" value={f.why_encore} onChange={set("why_encore")} required testId="ticketed-why" />
        <TextField label="Estimated event budget" value={f.estimated_budget} onChange={set("estimated_budget")} required testId="ticketed-budget" />
        <TextAreaField label="What does success for this event look like?" value={f.success_definition} onChange={set("success_definition")} required testId="ticketed-success" />
        <RadioField label="Have you ever had an event canceled or removed from a venue?" value={f.ever_canceled} onChange={set("ever_canceled")} options={YN} required testId="ticketed-canceled" />
        {f.ever_canceled === "Yes" && (
          <TextAreaField label="If yes, please explain" value={f.canceled_explanation} onChange={set("canceled_explanation")} required testId="ticketed-canceled-explanation" />
        )}
        <FileAttachmentField
          label="Supporting Documents"
          hint="Optional. Attach a script, rider, set plot, contract, marketing one-sheet, or any reference docs. PDF, Word, Excel, or images. Max 5 files, 10 MB each."
          value={f.attachments}
          onChange={set("attachments")}
          testId="ticketed-attachments"
        />
        <AcknowledgmentCheckbox checked={f.acknowledged} onChange={set("acknowledged")} testId="ticketed-acknowledged">
          {ACKNOWLEDGMENT_TEXT}
        </AcknowledgmentCheckbox>
      </Section>

      <div className="pt-6">
        <button type="submit" disabled={submitting} data-testid="ticketed-submit" className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-3">
          {submitting ? "Sending…" : "Submit Inquiry"}
          {!submitting && <Send size={16} />}
        </button>
      </div>
    </form>
  );
}
